import { OptionallyVisible } from 'components';
import { FormikErrors, setIn, useFormik } from 'formik';
import { useNotifications } from 'providers/notifications/useNotifications';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useGetCountriesListQuery } from 'services/assets';
import merchantsApi, { useCreateMerchantsBankAccountMutation, useGetTitanDataMutation } from 'services/merchants';
import {
  BridgeFiatRail,
  ExternalAccountType,
  FiatPayoutSettingsPayload,
  MerchantBankInfo,
  MerchantPayoutFlow,
} from 'services/merchants/types';

import { Button } from 'components/buttons';
import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import { FormField } from 'components/form/formField/formField';
import IconAdd from 'components/icons/add';
import { Input } from 'components/inputs';
import { SearchableSelect } from 'components/inputs/select/searchableSelect';
import { Select, SelectItem } from 'components/inputs/select/select';
import { Buttons } from 'components/modal/styles';

import { APIErrorTypes } from 'types/error';
import { ReduxToolkitError } from 'types/http';
import { NotificationSeverity } from 'types/notifications';

import { getServerSideValidationDetails } from 'utils/error';
import Logger from 'utils/logger';
import { replaceEmptyStringsWithUndefined } from 'utils/string';

import { usePayout } from '../../../payoutModal/context';
import { SetupPayoutStep, usePayoutSetupStep } from '../../../payoutModal/context/modal';
import IncompatibileRail from '../../../payoutModal/fiat/incompatibileRail';
import { LABELS as MAIN_LABELS } from '../../../payoutModal/fiat/keys';
import { ErrorText } from '../../../payoutModal/fiat/styles';
import { KEYS as PAYOUT_MODAL_KEYS } from '../../../payoutModal/keys';
import { Delimiter } from '../../../payoutModal/styles';
import { PayoutType } from '../../../payoutModal/types';
import { LABELS } from '../../keys';
import { Label, Section, SelectContainer, ThinModalButton } from '../form.styles';
import {
  FORM_FIELDS,
  KEYS,
  NESTED_ACCOUNT_FIELDS,
  SepaFormField,
  SepaFormNestedField,
  SepaFormValues,
  SepaNestedFormValues,
  categoryOptions,
  getPrefilledValues,
  getValidationSchema,
} from './sepa.keys';

type MerchantBankInfoSelectItem = SelectItem & { value: MerchantBankInfo };

const SepaBankAccountForm = () => {
  const { t } = useTranslation();
  const payout = usePayout();
  const payoutStep = usePayoutSetupStep();
  const previouslyProvidedValues: any = payout[PayoutType.Fiat][BridgeFiatRail.SEPA]?.form?.values;
  const [isIncompatiblePaymentRail, setIsIncompatiblePaymentRail] = useState(false);
  const { pushToast } = useNotifications();
  const [titanBankAccountSelectionActive, setTitanBankAccountSelectionActive] = useState(false);
  const [getTitanData, { data: titanData, isLoading: isLoadingTitanData }] = useGetTitanDataMutation();
  const [selectedTitanBankAccount, setSelectedTitanBankAccount] = useState<MerchantBankInfoSelectItem | null>(null);
  const [payoutSettingsMutation, { isLoading: isLoadingPayoutSettings }] = merchantsApi.useUpdateMerchantPayoutSettingsMutation();

  const [createBankAccount, { isLoading, error }] = useCreateMerchantsBankAccountMutation();
  const { data: countries = [] } = useGetCountriesListQuery();

  const mappedCountries: SelectItem[] = countries.map((country) => ({
    key: country.isoAlpha3,
    icon: country.iconUrl,
    label: country.name,
  }));

  const backButtonLabel = payoutStep.canGoBack() ? t(LABELS.ACTIONS.BACK) : t(LABELS.ACTIONS.CANCEL);

  const preparePayload = (values: SepaFormValues) => {
    const payload = {
      ...values,
      uuid: payout.merchant.uuid,
      paymentRail: BridgeFiatRail.SEPA,
      currency: KEYS.HARDCODED_CURRENCY,
      purposeOfFunds: KEYS.HARDCODED_PURPOSE_OF_FUNDS,
    };

    return payload;
  };

  const form = useFormik<SepaFormValues>({
    initialValues: previouslyProvidedValues ?? getPrefilledValues(),
    validationSchema: getValidationSchema(),
    onSubmit: async (values, formikHelpers) => {
      const errors = await formikHelpers.validateForm();
      if (Object.keys(errors).length > 0) {
        return;
      }

      const payload = preparePayload(replaceEmptyStringsWithUndefined(values));
      const bankAccountResponse = await createBankAccount(payload);
      if ('error' in bankAccountResponse) {
        const isIncompatiblePaymentRailError =
          (bankAccountResponse.error as ReduxToolkitError)?.data?.errorType === APIErrorTypes.IncompatiblePaymentRail;

        if (isIncompatiblePaymentRailError) {
          setIsIncompatiblePaymentRail(true);
          return;
        }

        const serverErrors = getServerSideValidationDetails(bankAccountResponse.error, KEYS.UNASSOCIATED_ERROR);
        // Bridge sends anything related to accoount as nested account.<anything> field
        // while our backend has mapped everything for SWIFT to iban.<anything>
        const mappedServerErrors = Object.fromEntries(
          Object.entries(serverErrors ?? {}).map(([key, value]) => [key.replace('account.', 'iban.') || key, value]),
        ) as any;
        const touchedFields = flattenKeys(mappedServerErrors).reduce((acc, path) => setIn(acc, path, true), {});

        form.setErrors(unflattenErrors(mappedServerErrors));
        form.setTouched(touchedFields, false);
        return;
      }

      const noNeedToUpdatePaymentRail = payout.merchant.payoutFlow === MerchantPayoutFlow.DIRECT;
      if (bankAccountResponse.data?.data?.uuid) {
        const updatePayoutSettingsPayload: FiatPayoutSettingsPayload = {
          merchantId: payout.merchant.uuid,
          payoutDestinationUuid: bankAccountResponse.data?.data?.uuid,
          payoutMemo: '',
          ...(!noNeedToUpdatePaymentRail && { paymentRail: BridgeFiatRail.SEPA }),
        };

        const payoutSettingsResponse = await payoutSettingsMutation(updatePayoutSettingsPayload);
        if ('error' in bankAccountResponse) {
          Logger.error('Unable to update main account after bank account was created:', JSON.stringify(payoutSettingsResponse));
        }
      }

      payoutStep.proceed();
    },
  });

  const hasErrors = Object.values(form.errors).length > 0;
  const accountNumberLabel =
    form.values[FORM_FIELDS.ACCOUNT_TYPE] === ExternalAccountType.IBAN
      ? t(LABELS.CREATE_MODAL.SECTIONS.ACCOUNT_NUMBER_SECTION.IBAN_TITLE)
      : t(LABELS.CREATE_MODAL.SECTIONS.ACCOUNT_NUMBER_SECTION.TITLE);

  const onSelectChange = (field: SepaFormField) => (value: SelectItem | null) => {
    if (!value?.key) {
      return;
    }

    onFormChange(field)(value.key);
  };

  const onFormChange = (field: SepaFormField) => (value: string) => {
    form.setFieldTouched(field, true);
    form.setFieldValue(field, value);
  };

  const onFormNestedChange = (field: SepaFormField) => (nestedField: SepaFormNestedField) => (value: string | undefined) => {
    form.setFieldTouched(`${field}.${nestedField}`, true);
    form.setFieldValue(field, {
      ...(form.values[field] as Record<SepaFormNestedField, string | undefined>),
      [nestedField]: value,
    });
  };

  const onNestedDontSentEmptyStringChange = (field: SepaFormField) => (nestedField: SepaFormNestedField) => (value: string | undefined) => {
    if (value === '') {
      onFormNestedChange(field)(nestedField)(undefined);
      return;
    }

    onFormNestedChange(field)(nestedField)(value);
  };

  const onSelectNestedChange = (field: SepaFormField) => (nestedField: SepaFormNestedField) => (value: SelectItem | null) => {
    if (!value?.key) {
      return;
    }

    onFormNestedChange(field)(nestedField)(value.key);
  };

  const getCurrentSelectItem =
    (listOfItems: SelectItem[]) =>
    (field: SepaFormField): SelectItem | undefined => {
      return listOfItems.find((item) => item.key === form.values[field]);
    };

  const getCurrentNestedSelectItem =
    (listOfItems: SelectItem[]) =>
    (field: SepaFormField, nestedField: SepaFormNestedField): SelectItem | undefined => {
      return listOfItems.find((item) => item.key === (form.values[field] as any)?.[nestedField]);
    };

  const getNestedFieldError = (field: SepaFormField, nestedField: SepaFormNestedField) => {
    const error = (form.errors[field] as FormikErrors<SepaNestedFormValues>)?.[nestedField];
    const touched = (form.touched[field] as FormikErrors<SepaNestedFormValues>)?.[nestedField];
    if (!error || !touched) {
      return undefined;
    }

    return error;
  };

  const getFieldError = (field: SepaFormField | FormikErrors<SepaNestedFormValues>): string | undefined => {
    if (typeof field === 'object') {
      return undefined;
    }

    if (!form.errors[field]) {
      return undefined;
    }

    if (!form.touched[field]) {
      return undefined;
    }

    return form.errors[field] as string | undefined;
  };

  const convertToCamelCase = (key: string) => key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

  const flattenKeys = (obj: any, prefix = ''): string[] => {
    if (typeof obj !== 'object' || obj === null) return [];

    const keys: string[] = [];

    for (const [key, value] of Object.entries(obj)) {
      // This is to handle Bridge passed errors, this should be handled on BE
      const camelCaseKey = convertToCamelCase(key);
      let path = '';
      if (Array.isArray(obj)) {
        path = `${prefix}[${camelCaseKey}]`;
      } else if (prefix) {
        path = `${prefix}.${camelCaseKey}`;
      } else {
        path = camelCaseKey;
      }

      if (typeof value === 'object' && value !== null) {
        keys.push(...flattenKeys(value, path));
      } else {
        keys.push(path);
      }
    }

    return keys;
  };

  const unflattenErrors = (flat: Record<string, any> | undefined) => {
    if (!flat) {
      return {};
    }

    const result: Record<string, any> = {};

    Object.entries(flat).forEach(([path, value]) => {
      const keys = path.replace(/\[\d+\]/g, '').split('.');
      keys.reduce((acc, key, i) => {
        // This is to handle Bridge passed errors, this should be handled on BE
        const camelCaseKey = convertToCamelCase(key);
        if (i === keys.length - 1) acc[camelCaseKey] = value;
        else acc[camelCaseKey] ??= {};
        return acc[camelCaseKey];
      }, result);
    });

    return result;
  };

  const fillFormValuesWithMerchantBankAccount = (merchantBankAccount: MerchantBankInfo) => {
    form.setValues({
      ...form.values,
      [FORM_FIELDS.IBAN]: {
        [NESTED_ACCOUNT_FIELDS.BIC]: merchantBankAccount.iban?.bic ?? '',
        [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: merchantBankAccount.iban?.accountNumber ?? '',
        [NESTED_ACCOUNT_FIELDS.ACCOUNT_COUNTRY]: merchantBankAccount.iban?.country ?? '',
      },
      [FORM_FIELDS.BUSINESS_NAME]: merchantBankAccount.businessName,
      [FORM_FIELDS.BANK_NAME]: merchantBankAccount.bankName,
      [FORM_FIELDS.ACCOUNT_OWNER_NAME]: merchantBankAccount.accountOwnerName,
      [FORM_FIELDS.ACCOUNT_OWNER_TYPE]: merchantBankAccount.accountOwnerType,
      [FORM_FIELDS.ADDRESS]: {
        [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: merchantBankAccount.address?.streetLine1 ?? '',
        [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: merchantBankAccount.address?.streetLine2 ?? '',
        [NESTED_ACCOUNT_FIELDS.CITY]: merchantBankAccount.address?.city ?? '',
        [NESTED_ACCOUNT_FIELDS.STATE]: merchantBankAccount.address?.state ?? '',
        [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: merchantBankAccount.address?.postalCode ?? '',
        [NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY]: merchantBankAccount.address?.country ?? '',
      },
      [FORM_FIELDS.CATEGORY]: merchantBankAccount.category,
      [FORM_FIELDS.SHORT_BUSINESS_DESCRIPTION]: merchantBankAccount.purposeOfFunds,
    });
  };

  const shift4Uuid = payout.merchant?.shift4MerchantId;
  const titanButtonContainer = document.getElementById(PAYOUT_MODAL_KEYS.MODAL_HEADER_ID);
  const showTitanDataInsertButton = payoutStep[PayoutType.Fiat] === SetupPayoutStep.AccountDetails && Boolean(shift4Uuid);

  const onGetTitanDataClick = async () => {
    if (!shift4Uuid) {
      return;
    }

    const response = await getTitanData({ uuid: shift4Uuid });
    if ('error' in response) {
      pushToast({
        severity: NotificationSeverity.error,
        message: t(LABELS.ERRORS.UNABLE_TO_GET_TITAN_DATA),
      });
      return;
    }

    const titanBankAcccounts = response.data?.data?.bankAccounts.filter((bankAccount) => bankAccount.paymentRail === BridgeFiatRail.SEPA);
    if (!titanBankAcccounts?.length) {
      pushToast({
        severity: NotificationSeverity.info,
        message: t(LABELS.ERRORS.NO_BANK_ACCOUNTS),
      });
      return;
    }

    if (titanBankAcccounts.length === 1) {
      fillFormValuesWithMerchantBankAccount(titanBankAcccounts[0]);

      return;
    }

    setTitanBankAccountSelectionActive(true);
  };

  useEffect(() => {
    payout.setPayoutState({
      ...payout,
      [PayoutType.Fiat]: {
        ...payout[PayoutType.Fiat],
        [BridgeFiatRail.SEPA]: {
          ...payout[PayoutType.Fiat][BridgeFiatRail.SEPA],
          form,
        },
      },
    });
  }, [form.values]);

  const onIncompatiblePaymentRailAcknowledge = () => {
    setIsIncompatiblePaymentRail(false);
  };

  if (titanBankAccountSelectionActive) {
    const titanBankAcccounts = titanData?.data?.bankAccounts ?? [];
    const mappedBankAccounts: MerchantBankInfoSelectItem[] = titanBankAcccounts.map((account) => ({
      key: account.uuid,
      label: `${account.bankName} •••• ${account.account?.accountNumber?.replaceAll('*', '')}`,
      value: account,
    }));

    const onBankSelect = (item: SelectItem | null) => {
      setSelectedTitanBankAccount(item as MerchantBankInfoSelectItem | null);
    };

    const onProceedWithExistingAccount = () => {
      fillFormValuesWithMerchantBankAccount(selectedTitanBankAccount?.value as MerchantBankInfo);
      setTitanBankAccountSelectionActive(false);
    };

    const onTitanBankAccountSelectCancel = () => {
      setTitanBankAccountSelectionActive(false);
    };

    return (
      <>
        <OptionallyVisible visible={Boolean(titanButtonContainer)}>
          {createPortal(
            <OptionallyVisible visible={showTitanDataInsertButton}>
              <ThinModalButton isLoading={isLoadingTitanData} secondary onClick={onGetTitanDataClick}>
                <IconAdd /> {t(MAIN_LABELS.ACTIONS.INSERT_TITAN_DATA)}
              </ThinModalButton>
            </OptionallyVisible>,
            titanButtonContainer || document.body,
          )}
        </OptionallyVisible>
        <Section>
          <Label>{t(MAIN_LABELS.ACCOUNT_SELECTION_LABEL, { currency: payout.merchant.mainFiatCurrency })}</Label>
          <Select<SelectItem, false>
            fullWidth
            value={selectedTitanBankAccount ?? undefined}
            items={mappedBankAccounts}
            onChange={onBankSelect}
            getItemLabel={(item) => item?.label ?? item?.key ?? ''}
            multiple={false}
            closeDropdownOnSelect
            placeholder={t(MAIN_LABELS.ACCOUNT_SELECTION_PLACEHOLDER)}
          />
        </Section>
        <Delimiter />
        <Buttons>
          <Button secondary onClick={onTitanBankAccountSelectCancel}>
            {t(LABELS.ACTIONS.BACK)}
          </Button>
          <Button disabled={!selectedTitanBankAccount} primary onClick={onProceedWithExistingAccount}>
            {t(LABELS.ACTIONS.ADD)}
          </Button>
        </Buttons>
      </>
    );
  }

  if (isIncompatiblePaymentRail) {
    return <IncompatibileRail onIncompatiblePaymentRailAcknowledge={onIncompatiblePaymentRailAcknowledge} />;
  }

  return (
    <>
      <OptionallyVisible visible={Boolean(titanButtonContainer)}>
        {createPortal(
          <OptionallyVisible visible={showTitanDataInsertButton}>
            <ThinModalButton isLoading={isLoadingTitanData} secondary onClick={onGetTitanDataClick}>
              <IconAdd /> {t(MAIN_LABELS.ACTIONS.INSERT_TITAN_DATA)}
            </ThinModalButton>
          </OptionallyVisible>,
          titanButtonContainer || document.body,
        )}
      </OptionallyVisible>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.BANK_NAME_SECTION.TITLE)}</Label>
        <FormField error={getFieldError(FORM_FIELDS.BANK_NAME)}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.BANK_NAME)}
            value={form.values[FORM_FIELDS.BANK_NAME] as string}
            onChange={onFormChange(FORM_FIELDS.BANK_NAME)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ACCOUNT_OWNER_NAME_SECTION.TITLE)}</Label>
        <FormField error={getFieldError(FORM_FIELDS.ACCOUNT_OWNER_NAME)}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.ACCOUNT_OWNER_NAME)}
            value={form.values[FORM_FIELDS.ACCOUNT_OWNER_NAME]}
            onChange={onFormChange(FORM_FIELDS.ACCOUNT_OWNER_NAME)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{accountNumberLabel}</Label>
        <FormField error={getNestedFieldError(FORM_FIELDS.IBAN, NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER)}>
          <Input
            onChange={onFormNestedChange(FORM_FIELDS.IBAN)(NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER)}
            value={form.values[FORM_FIELDS.IBAN][NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]}
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.ACCOUNT_NUMBER)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.BIC_SECTION.TITLE)}</Label>
        <FormField error={getNestedFieldError(FORM_FIELDS.IBAN, NESTED_ACCOUNT_FIELDS.BIC)}>
          <Input
            onChange={onFormNestedChange(FORM_FIELDS.IBAN)(NESTED_ACCOUNT_FIELDS.BIC)}
            value={form.values[FORM_FIELDS.IBAN][NESTED_ACCOUNT_FIELDS.BIC]}
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.BIC)}
          />
        </FormField>
      </Section>

      <OptionallyVisible visible={form.values[FORM_FIELDS.ACCOUNT_TYPE] === ExternalAccountType.IBAN}>
        <SelectContainer>
          <Label>{t(LABELS.CREATE_MODAL.SECTIONS.COUNTRY_SECTION.TITLE)}</Label>
          <FormField error={getNestedFieldError(FORM_FIELDS.IBAN, NESTED_ACCOUNT_FIELDS.ACCOUNT_COUNTRY)}>
            <SearchableSelect<SelectItem, false>
              fullWidth
              value={getCurrentNestedSelectItem(mappedCountries)(FORM_FIELDS.IBAN, NESTED_ACCOUNT_FIELDS.ACCOUNT_COUNTRY)}
              items={mappedCountries}
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.COUNTRY)}
              onChange={onSelectNestedChange(FORM_FIELDS.IBAN)(NESTED_ACCOUNT_FIELDS.ACCOUNT_COUNTRY)}
              getItemLabel={(item) => item.label}
              multiple={false}
              closeDropdownOnSelect
              error={Boolean(getNestedFieldError(FORM_FIELDS.IBAN, NESTED_ACCOUNT_FIELDS.ACCOUNT_COUNTRY))}
            />
          </FormField>
        </SelectContainer>
      </OptionallyVisible>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.BUSINESS_NAME_SECTION.TITLE)}</Label>
        <FormField error={getFieldError(FORM_FIELDS.BUSINESS_NAME)}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.BUSINESS_NAME)}
            value={form.values[FORM_FIELDS.BUSINESS_NAME]}
            onChange={onFormChange(FORM_FIELDS.BUSINESS_NAME)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ADDRESS_SECTION.TITLE)}</Label>
        <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.STREET_LINE_1)}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.STREET_LINE_1)}
            value={form.values[FORM_FIELDS.ADDRESS][NESTED_ACCOUNT_FIELDS.STREET_LINE_1]}
            onChange={onFormNestedChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.STREET_LINE_1)}
          />
        </FormField>

        <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.STREET_LINE_2)}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.STREET_LINE_2)}
            value={form.values[FORM_FIELDS.ADDRESS][NESTED_ACCOUNT_FIELDS.STREET_LINE_2]}
            onChange={onNestedDontSentEmptyStringChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.STREET_LINE_2)}
          />
        </FormField>

        <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.CITY)}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.CITY)}
            value={form.values[FORM_FIELDS.ADDRESS][NESTED_ACCOUNT_FIELDS.CITY]}
            onChange={onFormNestedChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.CITY)}
          />
        </FormField>

        <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.STATE)}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.STATE)}
            value={form.values[FORM_FIELDS.ADDRESS][NESTED_ACCOUNT_FIELDS.STATE]}
            onChange={onFormNestedChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.STATE)}
          />
        </FormField>

        <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.POSTAL_CODE)}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.POSTAL_CODE)}
            value={form.values[FORM_FIELDS.ADDRESS][NESTED_ACCOUNT_FIELDS.POSTAL_CODE]}
            onChange={onFormNestedChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.POSTAL_CODE)}
          />
        </FormField>

        <SelectContainer>
          <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY)}>
            <SearchableSelect<SelectItem, false>
              fullWidth
              value={getCurrentNestedSelectItem(mappedCountries)(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY)}
              items={mappedCountries}
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.COUNTRY)}
              onChange={onSelectNestedChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY)}
              getItemLabel={(item) => item.label}
              multiple={false}
              closeDropdownOnSelect
              error={Boolean(getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY))}
            />
          </FormField>
        </SelectContainer>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.CATEGORY_SECTION.TITLE)}</Label>
        <SelectContainer>
          <FormField error={getFieldError(FORM_FIELDS.CATEGORY)}>
            <Select<SelectItem>
              value={getCurrentSelectItem(categoryOptions)(FORM_FIELDS.CATEGORY)}
              items={categoryOptions}
              onChange={onSelectChange(FORM_FIELDS.CATEGORY)}
              getItemLabel={(item) => item.label}
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.CATEGORY)}
              closeDropdownOnSelect
              error={Boolean(getFieldError(FORM_FIELDS.CATEGORY))}
            />
          </FormField>
        </SelectContainer>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.SHORT_BUSINESS_DESCRIPTION_SECTION.TITLE)}</Label>
        <FormField error={getFieldError(FORM_FIELDS.SHORT_BUSINESS_DESCRIPTION)}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.SHORT_BUSINESS_DESCRIPTION)}
            value={form.values[FORM_FIELDS.SHORT_BUSINESS_DESCRIPTION]}
            onChange={onFormChange(FORM_FIELDS.SHORT_BUSINESS_DESCRIPTION)}
          />
        </FormField>
      </Section>

      <OptionallyVisible visible={Boolean((error as ReduxToolkitError)?.data?.errorMessage)}>
        <ErrorText>{(error as ReduxToolkitError)?.data?.errorMessage}</ErrorText>
      </OptionallyVisible>

      <Delimiter />
      <Buttons>
        <Button secondary onClick={payoutStep.goBack}>
          {backButtonLabel}
        </Button>
        <ButtonWithLoading
          isLoading={isLoading || isLoadingPayoutSettings}
          primary
          onClick={() => form.handleSubmit()}
          disabled={hasErrors}
        >
          {t(LABELS.ACTIONS.ADD)}
        </ButtonWithLoading>
      </Buttons>
    </>
  );
};

export default SepaBankAccountForm;
